// Realtime warning popup — listens to moderation_actions targeted at the current user.
// Now a FULLSCREEN blocking modal: user must tap "I Understand" to dismiss.
// On 'auto_temp_ban' or severe → also navigates home so the user is forced out of any active call.
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { AlertTriangle, ShieldAlert } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useApp } from "@/contexts/AppContext";

interface ActionRow {
  id: string;
  action: string;
  reason: string | null;
  meta: any;
  created_at: string;
}

export const WarningToast = () => {
  const { user } = useApp();
  const navigate = useNavigate();
  const [warning, setWarning] = useState<{ severity: "severe" | "moderate" | "minor"; message: string; isBan?: boolean } | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    console.log("[WarningToast] subscribing for user", user.id);

    const ch = supabase
      .channel(`warn-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "moderation_actions",
          filter: `target_user_id=eq.${user.id}`,
        },
        (payload) => {
          const a = payload.new as ActionRow;
          console.warn("[WarningToast] action received:", a);
          if (a.meta?.source === "ai_text" || a.reason?.startsWith("text:")) return;
          const severity = (a.meta?.severity as "severe" | "moderate" | "minor") ?? "minor";

          if (a.action === "auto_temp_ban" || a.action === "ban") {
            setWarning({
              severity: "severe",
              message: "Your account has been suspended for 24 hours due to unsafe behavior.",
              isBan: true,
            });
            // Force out of any active call
            setTimeout(() => navigate("/", { replace: true }), 100);
          } else if (a.action === "auto_warn" || a.action === "warn") {
            setWarning({
              severity,
              message: severity === "severe"
                ? "Your call was ended due to unsafe behavior. Repeated violations will result in a ban."
                : "You have been warned for violating community guidelines.",
            });
            if (severity === "severe") {
              setTimeout(() => navigate("/", { replace: true }), 100);
            }
          } else if (a.action === "dispute_approved") {
            setWarning({ severity: "minor", message: "Your appeal was approved. You can use the app again." });
          }
        }
      )
      .subscribe();

    return () => { void supabase.removeChannel(ch); };
  }, [user?.id, navigate]);

  // No auto-dismiss — user must explicitly acknowledge with "I Understand".

  return (
    <AnimatePresence>
      {warning && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          // Fullscreen, fully-blocking overlay. Cannot be dismissed except via the button.
          className="fixed inset-0 z-[300] bg-background/80 backdrop-blur-md flex items-center justify-center p-5"
        >
          <motion.div
            initial={{ scale: 0.9, y: 10 }}
            animate={{ scale: 1, y: 0 }}
            transition={{ type: "spring", damping: 22, stiffness: 280 }}
            className={`w-full max-w-sm rounded-3xl border-2 shadow-2xl p-6 text-center ${
              warning.severity === "severe"
                ? "bg-destructive/15 border-destructive"
                : "bg-yellow-500/15 border-yellow-500"
            }`}
          >
            <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
              warning.severity === "severe" ? "bg-destructive/25" : "bg-yellow-500/25"
            }`}>
              {warning.severity === "severe" ? (
                <ShieldAlert className="w-8 h-8 text-destructive" />
              ) : (
                <AlertTriangle className="w-8 h-8 text-yellow-500" />
              )}
            </div>
            <h2 className="font-display font-bold text-foreground text-xl mb-2">
              {warning.severity === "severe" ? "Action taken" : "Warning"}
            </h2>
            <p className="text-sm text-foreground/80 mb-6 leading-relaxed">
              {warning.message}
            </p>
            <button
              onClick={() => {
                // For BAN: reload so the app boots into the BannedScreen.
                // For WARN: just dismiss and return to the app.
                if (warning.isBan) {
                  window.location.reload();
                  return;
                }
                setWarning(null);
              }}
              className={`w-full py-3.5 rounded-2xl font-display font-bold text-sm transition-transform active:scale-[0.98] ${
                warning.severity === "severe"
                  ? "bg-destructive text-destructive-foreground"
                  : "bg-yellow-500 text-black"
              }`}
            >
              I Understand
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WarningToast;
